import type { ForestColorMode, LayerKey } from "../../store/useLayerVisibility";

/**
 * Что слой показывает на карте — выводится в левом сайдбаре под секцией
 * слоёв. На мобайле tooltip'ов нет (тач без hover), а текст всё равно
 * полезен — поэтому канонический источник описаний — здесь, не на чипе.
 *
 * Источники данных в текстах не упоминаются — они и так живут в секции
 * «Источники» внизу сайдбара / в /methodology. Описания фокусируются
 * на «что я вижу на карте».
 */
export const LAYER_DESCRIPTIONS: Record<LayerKey, { title: string; body: string }> = {
  // forest — placeholder, реальный текст выбирается по forestColorMode
  // в getForestDescription ниже (LayerInfoPanel зовёт её для key='forest').
  forest: {
    title: "Породы леса",
    body: "Раскрашивает выделы по преобладающей породе дерева — сосна, ель, берёза, осина и так далее.",
  },
  forecastChoropleth: {
    title: "Прогноз грибов",
    body: "Прогноз появления грибов по районам ЛО. Сейчас отключён — модель в разработке.",
  },
  water: {
    title: "Озёра",
    body: "Озёра и крупные водохранилища.",
  },
  waterway: {
    title: "Реки и ручьи",
    body: "Реки, ручьи, каналы, дренажные канавы — около 200 тысяч объектов в ЛО.",
  },
  wetland: {
    title: "Болота",
    body: "Болота и заболоченные земли.",
  },
  oopt: {
    title: "ООПТ",
    body: "Особо охраняемые природные территории — заказники, заповедники, парки. Сбор грибов и ягод часто ограничен.",
  },
  roads: {
    title: "Дороги",
    body: "Автомобильные дороги. Помогают спланировать подъезд к лесу.",
  },
  felling: {
    title: "Вырубки",
    body: "Зоны вырубок и лесохозяйственных работ.",
  },
  protective: {
    title: "Защитные леса",
    body: "Водоохранные, нерестовые и прочие защитные категории — режим использования отличается от эксплуатационных.",
  },
  soil: {
    title: "Почвы",
    body: "Почвенные полигоны масштаба 1:2.5М. При клике в попап подгружается тип почвы и параметры pH/гумуса.",
  },
  hillshade: {
    title: "Рельеф",
    body: "Цветной рельеф: гипсометрия по высоте × отмывка склонов.",
  },
  districts: {
    title: "Районы ЛО",
    body: "Границы 18 районов Ленинградской области.",
  },
  userSpots: {
    title: "Сохранённые места",
    body: "Ваши приватные точки с оценкой 1–5 и тегами. Доступно после входа через Яндекс.",
  },
};

/**
 * Лес — три режима, у каждого своё описание. Title и body разные:
 * у species говорим про породу, у bonitet — про продуктивность,
 * у age_group — про возрастные группы.
 */
export function getForestDescription(
  mode: ForestColorMode,
): { title: string; body: string } {
  switch (mode) {
    case "species":
      return {
        title: "Лес: породы",
        body: "Раскрашивает выделы по преобладающей породе дерева — сосна, ель, берёза, осина, ольха, дуб и другие.",
      };
    case "bonitet":
      return {
        title: "Лес: бонитет",
        body: "Оценка продуктивности насаждения по шкале от I (лучший) до V (худший). Зелёный — высокобонитетные леса, красный — низкобонитетные.",
      };
    case "age_group":
      return {
        title: "Лес: возраст",
        body: "Возрастные группы выделов: молодняки, средневозрастные, приспевающие, спелые, перестойные.",
      };
  }
}
